<template>
  <fieldset
    class="w-full min-w-0"
    :data-testid="dataTestid"
    :disabled="disabled"
  >
    <legend
      v-if="label"
      class="mb-2 text-sm font-bold text-semantic-surface-on-surface sm:text-base"
      :class="{ 'sr-only': hideLabel }"
    >
      {{ label }}
    </legend>

    <div class="flex items-start gap-2">
      <V2AtomsCountryCodeSelect
        :id="`${fieldId}-country`"
        ref="countryRef"
        :model-value="iso"
        :countries="countries"
        :disabled="disabled"
        :has-error="!!shownError"
        :data-testid="dataTestid ? `${dataTestid}-country` : ''"
        class="w-[7.5rem] shrink-0"
        @update:model-value="handleCountryChange"
      />
      <V2AtomsInput
        :id="`${fieldId}-number`"
        ref="numberRef"
        v-model="displayValue"
        :name="name"
        type="text"
        mobile-keyboard-type="tel"
        autocomplete="tel-national"
        :placeholder="placeholder || country?.placeholder"
        :error-message="shownError"
        :disabled="disabled"
        :aria-invalid="shownError ? 'true' : undefined"
        :aria-describedby="shownError ? errorId : undefined"
        :aria-label="label ? `${label} without country code` : 'Phone number without country code'"
        :data-testid="dataTestid ? `${dataTestid}-number` : 'v2-atoms-input'"
        wrapper-class="flex-1 min-w-0"
        @blur="handleBlur"
      />
    </div>

    <!-- The visible message is V2AtomsInput's error label; this copy is for screen readers. -->
    <p
      :id="errorId"
      class="sr-only"
      aria-live="polite"
    >
      {{ shownError }}
    </p>
  </fieldset>
</template>

<script setup>
import { DEFAULT_PHONE_COUNTRY, PHONE_COUNTRIES } from '~/constants/country.constants'
import { describePhoneError, formatNationalDisplay, formatPhone, isValidNational, parsePhone } from '#shared/phone'

defineOptions({
  name: 'V2MoleculesPhoneInput'
})

const props = defineProps({
  /** The whole number, in `valueFormat`. Empty while no number has been entered. */
  modelValue: {
    type: String,
    default: ''
  },
  /**
   * e164 (default): "+61412345678" — keeps the country, what Descope expects.
   * local: "0412345678" for AU, national digits elsewhere — what Member Hub stores.
   * national: "412345678" — digits after the dialling code.
   */
  valueFormat: {
    type: String,
    default: 'e164',
    validator: value => ['e164', 'local', 'national'].includes(value)
  },
  /** mobile (default) or any — `any` also accepts landlines, e.g. AU 02/03/07/08. */
  kind: {
    type: String,
    default: 'mobile',
    validator: value => ['mobile', 'any'].includes(value)
  },
  defaultCountry: {
    type: String,
    default: DEFAULT_PHONE_COUNTRY
  },
  countries: {
    type: Array,
    default: () => PHONE_COUNTRIES
  },
  label: {
    type: String,
    default: 'Mobile number'
  },
  hideLabel: {
    type: Boolean,
    default: false
  },
  /** Overrides the country's own placeholder. */
  placeholder: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: 'phone'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  /** An error from outside (e.g. the server). Shown in place of the field's own. */
  errorMessage: {
    type: String,
    default: ''
  },
  /** Base for the element ids; generated when not given. */
  id: {
    type: String,
    default: ''
  },
  dataTestid: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'update:countryCode', 'update:national', 'change', 'blur'])

const uid = useId()
const fieldId = computed(() => props.id || `phone-${uid}`)
const errorId = computed(() => `${fieldId.value}-error`)

const numberRef = ref(null)

const iso = ref(props.defaultCountry)
// Digits as the user typed them, a trunk "0" included, so the box shows what they entered.
const typedDigits = ref('')
// The user chose the country themselves: a leading "0" no longer switches it to AU.
const userPickedCountry = ref(false)
// A pasted number carried a dialling code none of `countries` uses.
const unsupported = ref(false)
const ownError = ref('')
// Set by blur or validate(); errors only show once the user has had a chance to finish.
const showErrors = ref(false)
// What this field last emitted, so the parent echoing it back is not re-parsed.
let lastEmitted = null

const country = computed(() => props.countries.find(item => item.iso === iso.value) || props.countries[0])

const parts = computed(() => ({
  iso: country.value?.iso,
  // The trunk "0" is how the number is dialled locally, never part of the number itself.
  national: typedDigits.value.startsWith('0') ? typedDigits.value.slice(1) : typedDigits.value,
  ...(unsupported.value ? { unsupported: true } : {})
}))

const shownError = computed(() => props.errorMessage || (showErrors.value ? ownError.value : ''))

const displayValue = computed({
  get: () => {
    // A "+…" that has not resolved to a supported country yet (still typing "+6", or a
    // code we do not support) stays visible as typed, plus included.
    if (unsupported.value) return `+${typedDigits.value}`
    return country.value ? formatNationalDisplay(typedDigits.value, country.value) : typedDigits.value
  },
  set: handleNumberInput
})

/*
 * Prefill. Any value the parent passes is split into country + number — "+919039293303"
 * shows India and "90392 93303", "0412345678" shows Australia and "0412 345 678". AU is
 * shown in local form to match its "0000 000 000" placeholder.
 */
function applyExternalValue (value) {
  const next = parsePhone(value, {
    countries: props.countries,
    defaultIso: props.defaultCountry,
    preferIso: userPickedCountry.value ? iso.value : undefined
  })
  const nextCountry = props.countries.find(item => item.iso === next.iso)

  iso.value = next.iso
  unsupported.value = !!next.unsupported
  typedDigits.value = nextCountry?.localTrunk && next.national ? `0${next.national}` : next.national
}

watch(() => props.modelValue, value => {
  if (value === lastEmitted) return
  applyExternalValue(value)
  if (showErrors.value) validate()
}, { immediate: true })

function handleNumberInput (text) {
  const raw = String(text ?? '')
  const caretDigits = digitsBeforeCaret(raw)

  if (raw.includes('+')) {
    // A typed or pasted international number picks its own country; the code moves into
    // the picker and only the number stays in the box.
    const next = parsePhone(raw, { countries: props.countries, defaultIso: props.defaultCountry })
    const onlyPlus = !raw.replace(/[^\d]/g, '')
    unsupported.value = onlyPlus || !!next.unsupported
    if (!unsupported.value) iso.value = next.iso
    typedDigits.value = next.national
  } else {
    let digits = raw.replace(/\D/g, '')
    unsupported.value = false

    // Requirement: a leading 0 is an Australian number — unless the user picked a country
    // themselves, in which case it is that country's own trunk prefix.
    if (digits.startsWith('0') && !userPickedCountry.value) iso.value = 'AU'

    /*
     * Capped at the longest number any offered country allows, not the selected one's: a
     * user often types the number before picking the country, and cutting a 10-digit
     * Philippine number to Australia's 9 would lose a digit for good. A number too long for
     * the selected country is reported by validation instead.
     */
    const longest = Math.max(...props.countries.map(item => item.maxLength || item.nationalLength || 0)) || 15
    digits = digits.slice(0, longest + (digits.startsWith('0') ? 1 : 0))
    typedDigits.value = digits
  }

  emitValue()
  if (showErrors.value) validate()
  restoreCaret(caretDigits)
}

function handleCountryChange (nextIso) {
  iso.value = nextIso
  userPickedCountry.value = true
  unsupported.value = false
  emitValue()
  if (showErrors.value) validate()
  nextTick(() => numberRef.value?.input?.focus?.())
}

function handleBlur (event) {
  if (typedDigits.value) showErrors.value = true
  if (showErrors.value) validate()
  emit('blur', event)
}

function emitValue () {
  /*
   * An unsupported code is emitted as typed ("+447911…"), not as "": the form that owns this
   * field can then tell "wrong country" from "nothing entered" when it validates the value.
   */
  const value = unsupported.value
    ? (typedDigits.value ? `+${typedDigits.value}` : '')
    : formatPhone(parts.value, props.valueFormat, props.countries)
  lastEmitted = value

  emit('update:modelValue', value)
  emit('update:countryCode', country.value?.phoneCode || '')
  emit('update:national', parts.value.national)
  emit('change', payload())
}

function payload () {
  const p = parts.value
  return {
    iso: p.iso,
    countryCode: country.value?.phoneCode || '',
    national: p.national,
    e164: unsupported.value ? '' : formatPhone(p, 'e164', props.countries),
    local: unsupported.value ? '' : formatPhone(p, 'local', props.countries),
    isComplete: !!p.national && !unsupported.value,
    isValid: isValidNational(p, props.countries, props.kind)
  }
}

function errorFor () {
  return describePhoneError(parts.value, props.countries, props.kind)
}

/** Shows the field's error, if any, and reports whether the number is valid. */
function validate () {
  showErrors.value = true
  ownError.value = errorFor()
  return !ownError.value
}

function digitsBeforeCaret (raw) {
  const input = numberRef.value?.input
  const caret = typeof input?.selectionStart === 'number' ? input.selectionStart : raw.length
  return raw.slice(0, caret).replace(/\D/g, '').length
}

// Spacing is re-applied on every keystroke; keep the caret after the same digit it followed.
function restoreCaret (digitCount) {
  nextTick(() => {
    const input = numberRef.value?.input
    if (!input || document.activeElement !== input) return

    const text = input.value
    let seen = 0
    let position = 0
    while (position < text.length && seen < digitCount) {
      if (/\d/.test(text[position])) seen += 1
      position += 1
    }
    input.setSelectionRange?.(position, position)
  })
}

defineExpose({ validate, getValue: payload, focus: () => numberRef.value?.input?.focus?.() })
</script>
